import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Plus, Search, Filter, FileText, Send, Copy, 
  CheckCircle, Clock, AlertCircle, Download, Eye, FileSpreadsheet, Bell, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { getQuotes, duplicateQuote, updateQuote, deleteQuote, getQuotePdfUrl, exportQuotesExcel, getExpiringQuotes, checkExpirations, formatMoney, formatDate, getApiError } from '../api';
import { useLanguage } from '../i18n';

const StatusBadge = ({ status }) => {
  const { t } = useLanguage();
  const styles = {
    draft: 'bg-gray-100 text-gray-700',
    sent: 'bg-blue-100 text-blue-700',
    accepted: 'bg-green-100 text-green-700',
    expired: 'bg-red-100 text-red-700',
  };
  
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
      {t(`quotes.status.${status}`)}
    </span>
  );
};

const QdQuotes = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expiringQuotes, setExpiringQuotes] = useState([]);
  const [exporting, setExporting] = useState(false);
  
  const user = JSON.parse(localStorage.getItem('qd_user') || '{}');
  const isAdmin = ['admin', 'superadmin'].includes(user.role);
  
  useEffect(() => {
    loadQuotes();
    loadExpiringQuotes();
  }, [statusFilter]);
  
  const loadQuotes = async () => {
    try {
      const response = await getQuotes(statusFilter);
      setQuotes(response.data);
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setLoading(false);
    }
  };
  
  const loadExpiringQuotes = async () => {
    try {
      const response = await getExpiringQuotes();
      setExpiringQuotes(response.data);
    } catch (error) {
      console.error('Failed to load expiring quotes:', error);
    }
  };
  
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await exportQuotesExcel({ status: statusFilter !== 'all' ? statusFilter : null });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quotes_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(language === 'lt' ? 'Excel failas atsisiųstas' : 'Excel file downloaded');
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setExporting(false);
    }
  };
  
  const handleCheckExpirations = async () => {
    try {
      const response = await checkExpirations();
      const msg = language === 'lt' 
        ? `Priminimai išsiųsti: ${response.data.reminders_sent}, Pasibaigę: ${response.data.quotes_expired}`
        : `Reminders sent: ${response.data.reminders_sent}, Expired: ${response.data.quotes_expired}`;
      toast.success(msg);
      loadQuotes();
      loadExpiringQuotes();
    } catch (error) {
      toast.error(getApiError(error));
    }
  };
  
  const handleDuplicate = async (quote, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const response = await duplicateQuote(quote.id);
      toast.success(language === 'lt' 
        ? `Pasiūlymas nukopijuotas: ${response.data.number}`
        : `Quote duplicated: ${response.data.number}`);
      loadQuotes();
    } catch (error) {
      toast.error(getApiError(error));
    }
  };
  
  const handleAccept = async (quote, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      await updateQuote(quote.id, { status: 'accepted' });
      toast.success(language === 'lt' ? 'Pasiūlymas pažymėtas kaip priimtas' : 'Quote marked as accepted');
      loadQuotes();
    } catch (error) {
      toast.error(getApiError(error));
    }
  };
  
  const handleDelete = async (quote, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const confirmMsg = language === 'lt'
      ? `Ar tikrai norite ištrinti pasiūlymą "${quote.number}"? Šis veiksmas negrįžtamas.`
      : `Are you sure you want to delete quote "${quote.number}"? This action cannot be undone.`;
    
    if (!confirm(confirmMsg)) return;
    
    try {
      await deleteQuote(quote.id);
      toast.success(language === 'lt' ? 'Pasiūlymas ištrintas' : 'Quote deleted');
      loadQuotes();
    } catch (error) {
      toast.error(getApiError(error));
    }
  };
  
  const handleDownloadPdf = (quote, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const token = localStorage.getItem('qd_token');
    const url = getQuotePdfUrl(quote.id);
    
    // Open in new tab with auth
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `${quote.number}.pdf`;
        a.click();
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(() => toast.error(language === 'lt' ? 'Nepavyko atsisiųsti PDF' : 'Failed to download PDF'));
  };
  
  const filteredQuotes = quotes.filter(quote => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      quote.number?.toLowerCase().includes(searchLower) ||
      quote.client_snapshot?.name?.toLowerCase().includes(searchLower)
    );
  });
  
  const statusOptions = [
    { value: 'all', labelKey: 'quotes.status.all', icon: FileText },
    { value: 'draft', labelKey: 'quotes.status.draft', icon: Clock },
    { value: 'sent', labelKey: 'quotes.status.sent', icon: Send },
    { value: 'accepted', labelKey: 'quotes.status.accepted', icon: CheckCircle },
    { value: 'expired', labelKey: 'quotes.status.expired', icon: AlertCircle },
  ];
  
  return (
    <div className="space-y-6">
      {/* Expiring Quotes Alert */}
      {expiringQuotes.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Bell className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <h3 className="font-medium text-amber-800">
                {language === 'lt' 
                  ? `${expiringQuotes.length} pasiūlymai baigia galioti per 25 dienas`
                  : `${expiringQuotes.length} quotes expiring within 25 days`}
              </h3>
              <div className="mt-2 space-y-1">
                {expiringQuotes.slice(0, 3).map((q) => (
                  <p key={q.id} className="text-sm text-amber-700">
                    <span className="font-medium">{q.number}</span> - {q.client_snapshot?.name} 
                    <span className="text-amber-500 ml-2">
                      ({q.days_left} {language === 'lt' ? 'd. liko' : 'days left'})
                    </span>
                  </p>
                ))}
                {expiringQuotes.length > 3 && (
                  <p className="text-sm text-amber-600">
                    {language === 'lt' ? `...ir dar ${expiringQuotes.length - 3}` : `...and ${expiringQuotes.length - 3} more`}
                  </p>
                )}
              </div>
              {isAdmin && (
                <button
                  onClick={handleCheckExpirations}
                  className="mt-3 text-sm text-amber-700 hover:text-amber-900 underline"
                >
                  {language === 'lt' ? 'Siųsti priminimus' : 'Send reminders'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('quotes.title')}</h1>
          <p className="text-gray-500">
            {filteredQuotes.length} {language === 'lt' ? 'pasiūlymų' : 'quotes'}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExportExcel}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            data-testid="export-excel-button"
          >
            <FileSpreadsheet size={18} />
            {exporting ? (language === 'lt' ? 'Eksportuojama...' : 'Exporting...') : t('common.excel')}
          </button>
          <Link
            to="/quote/quotes/new"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0F7D70] text-white rounded-lg hover:bg-[#0a5f55] transition-colors"
            data-testid="new-quote-button"
          >
            <Plus size={20} />
            {t('quotes.newQuote')}
          </Link>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder={t('quotes.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
              data-testid="search-input"
            />
          </div>
          
          {/* Status filter */}
          <div className="flex gap-2 overflow-x-auto pb-2 lg:pb-0">
            {statusOptions.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  onClick={() => setStatusFilter(option.value)}
                  className={`
                    flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors
                    ${statusFilter === option.value 
                      ? 'bg-[#14233F] text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
                  `}
                >
                  <Icon size={16} />
                  {t(option.labelKey)}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Quotes List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F7D70]" />
        </div>
      ) : filteredQuotes.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-100 text-center">
          <FileText size={48} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">{t('quotes.noQuotes')}</h3>
          <p className="text-gray-500 mb-4">
            {search 
              ? (language === 'lt' ? 'Pagal jūsų paiešką nieko nerasta' : 'No results found for your search')
              : t('quotes.createFirst')}
          </p>
          {!search && (
            <Link
              to="/quote/quotes/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0F7D70] text-white rounded-lg hover:bg-[#0a5f55] transition-colors"
            >
              <Plus size={20} />
              {t('quotes.newQuote')}
            </Link>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('quotes.table.number')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('quotes.table.client')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('quotes.table.status')}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('quotes.table.total')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('quotes.table.created')}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('quotes.table.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredQuotes.map((quote) => (
                  <tr 
                    key={quote.id}
                    onClick={() => navigate(`/quote/quotes/${quote.id}`)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">{quote.number}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-700">{quote.client_snapshot?.name || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={quote.status} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-medium text-gray-900">
                        {formatMoney(quote.totals?.total || 0)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-500">{formatDate(quote.created_at)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => handleDownloadPdf(quote, e)}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title={t('common.download')}
                        >
                          <Download size={18} />
                        </button>
                        <button
                          onClick={(e) => handleDuplicate(quote, e)}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title={t('common.duplicate')}
                        >
                          <Copy size={18} />
                        </button>
                        {quote.status === 'sent' && (
                          <button
                            onClick={(e) => handleAccept(quote, e)}
                            className="p-2 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors"
                            title={language === 'lt' ? 'Pažymėti kaip priimtą' : 'Mark as accepted'}
                          >
                            <CheckCircle size={18} />
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={(e) => handleDelete(quote, e)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title={t('common.delete')}
                            data-testid={`delete-quote-${quote.id}`}
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default QdQuotes;
